import { isGpsAddressField } from './gpsAddress';
import { observationKey } from './checklistObservations';
import type { Checklist, ChecklistField } from '@/hooks/useChecklistEditor';

type ChecklistValue = string | null | undefined;

export interface ChecklistSnapshotRecord {
  id: string;
  empresa_id: string;
  checklist_id: string;
  created_at?: number | string | null;
}

export interface ChecklistSnapshotFieldRecord {
  id: string;
  empresa_id: string;
  snapshot_id: string;
  original_campo_id?: string | null;
  field_key: string;
  grupo?: string | null;
  nome: string;
  tipo: string;
  ordem?: number | string | null;
  obrigatorio?: boolean | number | string | null;
  visivel?: boolean | number | string | null;
  config?: string | Record<string, unknown> | null;
}

export interface ChecklistValueRecord {
  id?: string;
  empresa_id: string;
  checklist_id: string;
  campo_id?: string | null;
  snapshot_field_id?: string | null;
  valor?: ChecklistValue;
  observacao?: string | null;
  updated_at?: number | string | null;
}

export interface ChecklistHistoricalReadRepository {
  listSnapshots(companyId: string, checklistId: string): Promise<ChecklistSnapshotRecord[]>;
  listSnapshotFields(companyId: string, snapshotId: string): Promise<ChecklistSnapshotFieldRecord[]>;
  listValues(companyId: string, checklistId: string): Promise<ChecklistValueRecord[]>;
}

export interface ChecklistSnapshotFeatureProvider {
  isEnabled(companyId: string): Promise<boolean | null> | boolean | null;
}

export type ChecklistReadSource = 'current_template' | 'snapshot';

export interface ChecklistHistoricalReadResult {
  checklistId: string;
  companyId: string;
  source: ChecklistReadSource;
  snapshotId: string | null;
  fields: ChecklistField[];
  values: Record<string, string>;
  readOnly: boolean;
}

export interface ResolveChecklistHistoricalReadInput {
  checklist: Checklist;
  currentFields: ChecklistField[];
  currentValues: Record<string, ChecklistValue> | null;
  templateRetired?: boolean;
  lifecycle?: 'existing' | 'new';
}

/**
 * Dependências estruturais que alteram o resultado da leitura histórica.
 *
 * A numeração do checklist é atualizada separadamente pelo Realtime e não
 * muda o snapshot, os campos ou a regra de somente leitura do editor.
 */
export const getChecklistHistoricalReadDependencies = (
  checklist: Checklist | null,
): readonly [
  string | null,
  string | null,
  string | null,
  string | null,
  string | null,
  string | number | null,
] => [
  checklist?.id ?? null,
  checklist?.empresa_id ?? null,
  checklist?.template_id ?? null,
  checklist?.snapshot_id ?? null,
  checklist?.status ?? null,
  checklist?.retired_at ?? null,
];

export class ChecklistSnapshotIncompleteError extends Error {
  constructor(checklistId: string) {
    super(`Snapshot historico incompleto para o checklist ${checklistId}.`);
    this.name = 'ChecklistSnapshotIncompleteError';
  }
}

const asBoolean = (value: ChecklistSnapshotFieldRecord['visivel'], fallback: boolean): boolean => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = value.trim().toLowerCase();
  return normalized !== 'false' && normalized !== '0';
};

const parseConfig = (value: ChecklistSnapshotFieldRecord['config']): Record<string, unknown> => {
  if (!value) return {};
  if (typeof value === 'object') return { ...value };
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const sortFields = (fields: ChecklistField[]): ChecklistField[] => [...fields].sort((left, right) => {
  const orderDifference = Number(left.ordem ?? 0) - Number(right.ordem ?? 0);
  if (orderDifference !== 0) return orderDifference;
  return String(left.id).localeCompare(String(right.id));
});

const currentResult = (
  checklist: Checklist,
  fields: ChecklistField[],
  values: Record<string, ChecklistValue> | null,
  existing = false,
  templateRetired = false,
): ChecklistHistoricalReadResult => ({
  checklistId: checklist.id,
  companyId: String(checklist.empresa_id ?? ''),
  source: 'current_template',
  snapshotId: null,
  fields: sortFields(fields.filter(field => field.retired_at == null
    || (existing && typeof values?.[field.id] === 'string')).map(field => ({
      ...field, ...(field.retired_at != null || templateRetired ? { historical_read_only: true } : {}),
    }))),
  values: Object.fromEntries(
    Object.entries(values ?? {}).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
  ),
  readOnly: templateRetired,
});

const chooseSnapshot = (
  snapshots: ChecklistSnapshotRecord[],
  companyId: string,
  checklistId: string
): ChecklistSnapshotRecord | null => {
  const scoped = snapshots.filter(snapshot => (
    snapshot.empresa_id === companyId && snapshot.checklist_id === checklistId
  ));
  if (scoped.length === 0) return null;
  if (scoped.length > 1) throw new ChecklistSnapshotIncompleteError(checklistId);
  return scoped[0];
};

const updatedAtTime = (value: ChecklistValueRecord['updated_at']): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const referencePriority = (
  value: ChecklistValueRecord,
  field: ChecklistSnapshotFieldRecord
): number => {
  if (value.snapshot_field_id === field.id) return 2;
  if (value.campo_id === field.id) return 1;
  if (field.original_campo_id && value.campo_id === field.original_campo_id) return 0;
  return -1;
};

export class ChecklistHistoricalReadService {
  constructor(
    private readonly repository: ChecklistHistoricalReadRepository,
    private readonly featureProvider: ChecklistSnapshotFeatureProvider
  ) {}

  async resolve(input: ResolveChecklistHistoricalReadInput): Promise<ChecklistHistoricalReadResult> {
    const checklistId = String(input.checklist.id ?? '').trim();
    const companyId = String(input.checklist.empresa_id ?? '').trim();
    if (!checklistId || !companyId) {
      throw new Error('Checklist sem identificacao de empresa para leitura historica.');
    }

    if (input.lifecycle === 'new') {
      return currentResult(input.checklist, input.currentFields, input.currentValues);
    }
    const featureDecision = await this.featureProvider.isEnabled(companyId);
    // Cache ausente após upgrade não invalida o histórico já persistido localmente.
    if (featureDecision === false) {
      return currentResult(input.checklist, input.currentFields, input.currentValues, true, input.templateRetired);
    }

    const snapshot = chooseSnapshot(
      await this.repository.listSnapshots(companyId, checklistId),
      companyId,
      checklistId
    );
    if (featureDecision === null && input.checklist.snapshot_id && input.checklist.snapshot_id !== snapshot?.id) {
      throw new ChecklistSnapshotIncompleteError(checklistId);
    }
    if (!snapshot) {
      const currentFieldIds = new Set(input.currentFields.map(field => String(field.id)));
      const existingValues = (await this.repository.listValues(companyId, checklistId))
        .filter(value => value.empresa_id === companyId && value.checklist_id === checklistId);
      const hasHistoricalReference = existingValues.some(value => (
        Boolean(value.snapshot_field_id) ||
        !value.campo_id ||
        !currentFieldIds.has(String(value.campo_id))
      ));
      if (hasHistoricalReference) {
        throw new ChecklistSnapshotIncompleteError(checklistId);
      }
      return currentResult(input.checklist, input.currentFields, input.currentValues, true, input.templateRetired);
    }

    const snapshotFields = (await this.repository.listSnapshotFields(companyId, snapshot.id))
      .filter(field => field.empresa_id === companyId && field.snapshot_id === snapshot.id);
    if (snapshotFields.length === 0) {
      throw new ChecklistSnapshotIncompleteError(checklistId);
    }

    const duplicateIds = new Set<string>();
    const seenIds = new Set<string>();
    snapshotFields.forEach(field => {
      if (seenIds.has(field.id)) duplicateIds.add(field.id);
      seenIds.add(field.id);
    });
    if (duplicateIds.size > 0) {
      throw new ChecklistSnapshotIncompleteError(checklistId);
    }

    const currentFieldsById = new Map(input.currentFields.map(field => [field.id, field]));
    const fields = sortFields(snapshotFields.map(field => ({
      ...parseConfig(field.config),
      // A ajuda GPS vem do campo atual; não participa do catálogo imutável.
      endereco_gps: field.tipo === 'text' && isGpsAddressField(currentFieldsById.get(field.original_campo_id ?? '') ?? { tipo: '' }) ? 1 : null,
      id: field.id,
      grupo: field.grupo || 'Geral',
      nome: field.nome,
      tipo: field.tipo,
      ordem: Number(field.ordem ?? 0),
      obrigatorio: asBoolean(field.obrigatorio, false),
      visivel: asBoolean(field.visivel, true),
      original_campo_id: field.original_campo_id ?? null,
      snapshot_field_id: field.id,
      field_key: field.field_key,
      historical_snapshot: true,
      // Aposentadoria do modelo bloqueia escrita; a do campo físico não invalida o snapshot canônico.
      historical_read_only: input.templateRetired === true,
    })));

    const values = (await this.repository.listValues(companyId, checklistId))
      .filter(value => value.empresa_id === companyId && value.checklist_id === checklistId);
    if (featureDecision === null && values.some(value => (
      value.snapshot_field_id
        ? !seenIds.has(value.snapshot_field_id)
        : !snapshotFields.some(field => referencePriority(value, field) >= 0)
    ))) {
      throw new ChecklistSnapshotIncompleteError(checklistId);
    }
    const resolvedValues: Record<string, string> = {};
    snapshotFields.forEach(field => {
      const value = values.reduce<ChecklistValueRecord | undefined>((selected, candidate) => {
        const candidatePriority = referencePriority(candidate, field);
        if (candidatePriority < 0) return selected;
        if (!selected) return candidate;
        const timeDifference = updatedAtTime(candidate.updated_at) - updatedAtTime(selected.updated_at);
        if (timeDifference !== 0) return timeDifference > 0 ? candidate : selected;
        return candidatePriority > referencePriority(selected, field) ? candidate : selected;
      }, undefined);
      if (value && typeof value.valor === 'string') resolvedValues[field.id] = value.valor;
      if (value?.observacao !== undefined) resolvedValues[observationKey(field.id)] = value.observacao ?? '';
    });

    return {
      checklistId,
      companyId,
      source: 'snapshot',
      snapshotId: snapshot.id,
      fields,
      values: resolvedValues,
      readOnly: input.templateRetired === true || input.checklist.status !== 'in_progress' || input.checklist.retired_at != null,
    };
  }
}
